import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TimeScrollWheel, { Ampm } from "./TimeScrollWheel";

interface TimeWheelDrawerProps {
  open: boolean;
  onClose: () => void;
  is24h?: boolean;
  setIs24h?: (value: boolean) => void;
  initialHour: number;
  initialMinute: number;
  initialAmpm?: Ampm;
  onSave: (hour: number, minute: number, ampm: Ampm) => void;
  title: string;
  saveLabel?: string;
  showModeToggle?: boolean;
}

function TimeWheelDrawer({
  open,
  onClose,
  is24h = true,
  setIs24h,
  initialHour,
  initialMinute,
  initialAmpm = "AM",
  onSave,
  title,
  saveLabel,
  showModeToggle = true,
}: TimeWheelDrawerProps) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1400 }}
      ModalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true,
      }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          px: 2,
          pt: 2.5,
          pb: 1.5,
          height: "min(90dvh, 720px)",
          maxHeight: "min(90dvh, 720px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        sx={{ position: "absolute", top: 12, right: 12 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box sx={{ textAlign: "center", mb: showModeToggle ? 2 : 1.5 }}>
        <Typography fontWeight={700} fontSize="16px">
          {title}
        </Typography>
      </Box>

      {showModeToggle && setIs24h && (
        <Box sx={{ display: "flex", gap: 1, mb: 2.5, justifyContent: "center" }}>
          {(["12h", "24h"] as const).map((mode) => {
            const active = is24h ? mode === "24h" : mode === "12h";

            return (
              <Box
                key={mode}
                onClick={() => setIs24h(mode === "24h")}
                sx={{
                  px: 2.5,
                  py: 1,
                  borderRadius: "20px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 700,
                  transition: "all 0.15s ease",
                  bgcolor: active ? "primary.main" : "transparent",
                  color: active ? "#fff" : "text.secondary",
                  border: "1px solid",
                  borderColor: active ? "primary.main" : "#E0E0E0",
                }}
              >
                {mode}
              </Box>
            );
          })}
        </Box>
      )}

      <TimeScrollWheel
        initialHour={initialHour}
        initialMinute={initialMinute}
        initialAmpm={initialAmpm}
        is24h={is24h}
        onSave={onSave}
        saveLabel={saveLabel}
      />
    </Drawer>
  );
}

export default TimeWheelDrawer;
